import type { TechnicalDetailsEntry } from "../components/TechnicalDetails";
import type { ProjectDashboardCard } from "../viewmodel/projectsDashboardAdapter";

export const readinessClass = (
  readiness: ProjectDashboardCard["exportReadiness"]
): string => {
  if (readiness === "blocked") return "bg-[#F6E7E4] text-[#BC4A3C]";
  if (readiness === "not_ready") return "bg-[#F6EEDD] text-[#8A6A2E]";
  return "bg-[#EAF1E6] text-[#3F6235]";
};

const routeLabel = (project: ProjectDashboardCard): string =>
  project.routeKind === "fullWorkspace" ? "Full workspace" : "Summary view";

export const projectBomUnitCount = (project: ProjectDashboardCard): number =>
  project.bomUnitCount ??
  project.supplySourceRollup.titan +
    project.supplySourceRollup.fabricated +
    project.supplySourceRollup.thirdParty +
    project.supplySourceRollup.inHouse;

export const physicalSummaryMetrics = (project: ProjectDashboardCard) =>
  [
    ["Controllers", project.controllerCount],
    ["Devices", project.deviceCount],
    ["Infrastructure", project.infraCount],
    ["Item lines", project.bomRowCount],
    ["BOM units", projectBomUnitCount(project)]
  ] as const;

export const itemSummaryMetrics = (project: ProjectDashboardCard) =>
  [
    ["Need your decision", project.needsUserDecisionCount],
    ["Blocking risks", project.blockingRiskCount],
    ["Clarifications", project.clientClarificationCount]
  ] as const;

export const projectNextAction = (project: ProjectDashboardCard): string => {
  if (project.blockingRiskCount > 0) return "Review blocking risks before export.";
  if (project.needsUserDecisionCount > 0) {
    return "Review the items that need your decision.";
  }
  if (project.exportReadiness === "ready") {
    return "Open the project and continue to the deliverable bundle.";
  }
  return "Open the project and review the remaining inputs.";
};

export const projectTechnicalSources = (
  project: ProjectDashboardCard
): TechnicalDetailsEntry[] => [
  {
    label: "Project route",
    detail: routeLabel(project),
    meta: project.edgeIncluded ? "Edge included" : "Edge removed",
    tone: "info"
  },
  {
    label: "Edge capabilities",
    detail: project.edgeCapabilities.join(" · "),
    meta: project.edgeDefaultApplied
      ? project.edgeInclusionWhy
      : project.edgeInclusionWhere,
    tone: "info"
  },
  { label: "Supply mix", detail: project.supplyMixLabel, tone: "neutral" }
];

export const projectTechnicalChecks = (
  project: ProjectDashboardCard
): TechnicalDetailsEntry[] => [
  ...physicalSummaryMetrics(project).map(
    ([label, value]) =>
      ({
        label,
        detail: value.toString(),
        tone: "neutral"
      }) satisfies TechnicalDetailsEntry
  ),
  { label: "BOM rows", detail: project.bomRowCount.toString(), tone: "neutral" },
  {
    label: "Items to check",
    detail: project.anomalyCount.toString(),
    tone: project.anomalyCount > 0 ? "warning" : "success"
  },
  {
    label: "Open decisions",
    detail: project.openDecisionCount.toString(),
    meta: `${project.needsUserDecisionCount} need your decision`,
    tone: project.needsUserDecisionCount > 0 ? "warning" : "success"
  }
];

export const projectTechnicalRawData = (project: ProjectDashboardCard) => ({
  id: project.id,
  routeKind: project.routeKind,
  edgePresent: project.edgePresent,
  edgeIncluded: project.edgeIncluded,
  edgeDefaultApplied: project.edgeDefaultApplied,
  edgeInclusionWhy: project.edgeInclusionWhy,
  edgeInclusionWhere: project.edgeInclusionWhere,
  edgeCapabilities: project.edgeCapabilities,
  controllerCount: project.controllerCount,
  deviceCount: project.deviceCount,
  infraCount: project.infraCount,
  bomRowCount: project.bomRowCount,
  bomUnitCount: projectBomUnitCount(project),
  anomalyCount: project.anomalyCount,
  supplySourceRollup: project.supplySourceRollup,
  openDecisionCount: project.openDecisionCount,
  needsUserDecisionCount: project.needsUserDecisionCount,
  blockingRiskCount: project.blockingRiskCount
});
